import React, { useEffect, useState } from 'react';
import { loadMigrationCheckpoint, MigrationCheckpoint } from '../idb/migration-utils';
import { Modal, Progress } from 'antd';
import './MigrationProgress.scss';

interface MigrationProgressProps {
  visible: boolean;
  onComplete?: () => void;
}

export const MigrationProgress: React.FC<MigrationProgressProps> = ({ 
  visible, 
  onComplete 
}) => {
  const [checkpoint, setCheckpoint] = useState<MigrationCheckpoint | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!visible) return;

    const interval = setInterval(() => {
      const cp = loadMigrationCheckpoint();
      if (cp) {
        setCheckpoint(cp);
        const pct = cp.totalRecords > 0 
          ? Math.round((cp.recordsProcessed / cp.totalRecords) * 100)
          : 0;
        setProgress(pct);

        if (cp.stage === 'complete') {
          clearInterval(interval);
          setTimeout(() => {
            onComplete?.();
          }, 1000);
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [visible, onComplete]);

  if (!visible || !checkpoint) return null;

  const getStageLabel = (stage: MigrationCheckpoint['stage']): string => {
    switch (stage) {
      case 'backup': return 'Creating backup...';
      case 'copy_chunks': return 'Migrating chunks...';
      case 'copy_uploads': return 'Migrating uploads...';
      case 'verify': return 'Verifying integrity...';
      case 'complete': return 'Migration complete!';
      default: return 'Processing...';
    }
  };

  return (
    <Modal
      open={visible}
      closable={false}
      footer={null}
      centered
      className="migration-progress-modal"
    >
      <div className="migration-progress">
        <div className="migration-header">
          <h3>🔄 Upgrading Database</h3>
          <p>Please wait while we upgrade your local database to v5...</p>
        </div>

        <div className="migration-stage">
          <span className="stage-label">{getStageLabel(checkpoint.stage)}</span>
          <span className="stage-progress">
            {checkpoint.recordsProcessed} / {checkpoint.totalRecords} records
          </span>
        </div>

        <Progress 
          percent={progress} 
          status={checkpoint.stage === 'complete' ? 'success' : 'active'}
          strokeColor={{
            '0%': '#667eea',
            '100%': '#764ba2',
          }}
        />

        <div className="migration-info">
          <p>
            {checkpoint.stage === 'complete' 
              ? '✅ Your data has been successfully migrated to the new format.'
              : 'This is a one-time upgrade and will only take a moment.'}
          </p>
        </div>
      </div>
    </Modal>
  );
};
